import type { ReactElement } from 'react'
import { SessionNodeDto, PendingInputDto } from '@api/types/session'
import { WorkflowNodeType } from '../../../types/workflow.types'
import AutoAdvanceNode from './AutoAdvanceNode'
import QuestionAnswerInput from './QuestionAnswerInput'
import NumpadInput from './NumpadInput'
import CodexInput from './CodexInput'
import QrScannerInput from './QrScannerInput'
import ModeratorApprovalWait from './ModeratorApprovalWait'
import ExitNode from './ExitNode'

interface NodeRendererProps {
  node: SessionNodeDto
  pendingInput: PendingInputDto | null
  sessionId: string | null
  onSubmitSuccess?: () => void
  onClose?: () => void
}

// Map inputType strings to challenge components
// Includes aliases for backend enum names that may differ from frontend types
function renderChallengeByInputType(
  inputType: string,
  node: SessionNodeDto,
  pendingInput: PendingInputDto,
  sessionId: string | null,
  onSubmitSuccess?: () => void,
  onClose?: () => void,
): ReactElement | null {
  switch (inputType) {
    case 'QuestionAnswer':
      return (
        <QuestionAnswerInput
          node={node}
          pendingInput={pendingInput}
          sessionId={sessionId}
          onSubmitSuccess={onSubmitSuccess}
          onClose={onClose}
        />
      )
    case 'Numpad':
    case 'NumericInput':
      return (
        <NumpadInput
          node={node}
          pendingInput={pendingInput}
          sessionId={sessionId}
          onSubmitSuccess={onSubmitSuccess}
          onClose={onClose}
        />
      )
    case 'QRCode':
    case 'QrCode':
      return (
        <QrScannerInput
          node={node}
          pendingInput={pendingInput}
          sessionId={sessionId}
          onSubmitSuccess={onSubmitSuccess}
          onClose={onClose}
        />
      )
    case 'Codex':
    case 'CodexInput':
      return (
        <CodexInput
          node={node}
          pendingInput={pendingInput}
          sessionId={sessionId}
          onSubmitSuccess={onSubmitSuccess}
          onClose={onClose}
        />
      )
    case 'ModeratorApproval':
      return (
        <ModeratorApprovalWait
          node={node}
          pendingInput={pendingInput}
          onClose={onClose}
        />
      )
    default:
      return null
  }
}

// Map node type to challenge component as fallback
function renderChallengeByNodeType(
  nodeType: WorkflowNodeType,
  node: SessionNodeDto,
  pendingInput: PendingInputDto,
  sessionId: string | null,
  onSubmitSuccess?: () => void,
  onClose?: () => void,
): ReactElement | null {
  switch (nodeType) {
    case WorkflowNodeType.QuestionAnswer:
      return (
        <QuestionAnswerInput
          node={node}
          pendingInput={pendingInput}
          sessionId={sessionId}
          onSubmitSuccess={onSubmitSuccess}
          onClose={onClose}
        />
      )
    case WorkflowNodeType.NumericInput:
      return (
        <NumpadInput
          node={node}
          pendingInput={pendingInput}
          sessionId={sessionId}
          onSubmitSuccess={onSubmitSuccess}
          onClose={onClose}
        />
      )
    case WorkflowNodeType.QRCode:
      return (
        <QrScannerInput
          node={node}
          pendingInput={pendingInput}
          sessionId={sessionId}
          onSubmitSuccess={onSubmitSuccess}
          onClose={onClose}
        />
      )
    case WorkflowNodeType.CodexInput:
      return (
        <CodexInput
          node={node}
          pendingInput={pendingInput}
          sessionId={sessionId}
          onSubmitSuccess={onSubmitSuccess}
          onClose={onClose}
        />
      )
    case WorkflowNodeType.ModeratorApproval:
      return (
        <ModeratorApprovalWait
          node={node}
          pendingInput={pendingInput}
          onClose={onClose}
        />
      )
    default:
      return null
  }
}

export default function NodeRenderer({
  node,
  pendingInput,
  sessionId,
  onSubmitSuccess,
  onClose,
}: NodeRendererProps) {
  const nodeType = node.nodeType as WorkflowNodeType

  // Exit nodes - show completion status
  if (
    nodeType === WorkflowNodeType.Exit ||
    nodeType === WorkflowNodeType.ExitSuccess ||
    nodeType === WorkflowNodeType.ExitFailed ||
    nodeType === WorkflowNodeType.ExitAbandoned
  ) {
    return <ExitNode node={node} />
  }

  // Challenge nodes with pending input
  if (pendingInput) {
    // Try matching by inputType string first
    const byInputType = renderChallengeByInputType(pendingInput.inputType, node, pendingInput, sessionId, onSubmitSuccess, onClose)
    if (byInputType) return byInputType

    // Fallback: match by node type (handles cases where API returns unexpected inputType string)
    const byNodeType = renderChallengeByNodeType(nodeType, node, pendingInput, sessionId, onSubmitSuccess, onClose)
    if (byNodeType) return byNodeType
  }

  // Auto-advance nodes (content display)
  // This includes: Start, FormattedMessage, TextClue, PoiLocation, Audio, Video, Image
  return <AutoAdvanceNode node={node} />
}
